import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TimezoneData from "../../../assets/data/TimezoneData";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import CustomInput from "./../../../components/CustomInput";
import CustomSelect from "./../../../components/CustomSelect";
import { addEventLog, addEventLogBulk } from "../../../axios/Eventlog/EventLogApi";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { DateTime } from "luxon";
import { useAuth } from "../../../context/AuthContext";
import CustomSelectRosterSelectUser from "../../../components/CustomSelectRosterSelectUser";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const TEST_MODE = false; // 🔒

const AddMultiEventDialog = ({
  onClose,
  usersData = [],
  shiftsData = [],
  onRefresh,
  selectedDate,
  preselectedUserIds = [],
  preselectedShiftIds,
}) => {
  const { genaralSettingTimeZone, dateTimeFormat } = useAuth();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);

  const normalizeTime = (time) => {
    if (!time) return null;

    if (/^\d{2}:\d{2}$/.test(time)) {
      return `${time}:00`;
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    return null;
  };

  useEffect(() => {
    if (Array.isArray(preselectedShiftIds)) {
      setSelectedShiftIds(preselectedShiftIds);
    }
  }, [preselectedShiftIds]);

  const timezoneData = TimezoneData();

  const buildAttendanceDateTimeUTC = (date, time) => {
    if (!date || !time || !genaralSettingTimeZone) return null;

    const yyyyMMdd = dayjs(date).format("YYYY-MM-DD");

    const companyDateTime = dayjs.tz(
      `${yyyyMMdd} ${time}`,
      "YYYY-MM-DD HH:mm:ss",
      genaralSettingTimeZone
    );

    if (!companyDateTime.isValid()) return null;

    return companyDateTime.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
  };

  const initialFormData = {
    eventType: 1,
    users: [],
    selectDate: selectedDate,
    selectTime: DateTime.local().toFormat("HH:00:00"),
    timeZone: DateTime.local().toFormat("ZZ"),
    shift: shiftsData?.[0]?.shiftId ?? "",
    shiftDate: new Date(),
    description: "",
  };

  // const [formData, setFormData] = useState(initialFormData);

  const [formData, setFormData] = useState(() => {
    const initial = { ...initialFormData };

    const {
      missingCheckInUserIds = [],
      missingCheckOutUserIds = []
    } = preselectedUserIds || {};

    const sourceIds =
      Number(initial.eventType) === 1
        ? missingCheckInUserIds
        : missingCheckOutUserIds;

    return {
      ...initial,
      users: [...new Set((sourceIds || []).filter(Boolean))],
    };
  });

  const {
    missingCheckInUserIds = [],
    missingCheckOutUserIds = [],
  } = preselectedUserIds || {};

  const currentMissingIds =
    Number(formData.eventType) === 1
      ? missingCheckInUserIds
      : missingCheckOutUserIds;

  const filteredMissingUsers = usersData.filter((u) =>
    currentMissingIds.includes(u.userId)
  );

  useEffect(() => {
    if (Array.isArray(preselectedUserIds) && preselectedUserIds.length > 0) {
      setFormData(prev => ({ ...prev, users: [...new Set(preselectedUserIds.filter(Boolean))] }));
    }
  }, [preselectedUserIds]);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleCloseDialog = () => onClose();

  const handleDataChange = (name, newData) => {
    setFormData((prev) => ({ ...prev, [name]: newData }));
  };

  const MAX_REASON_LENGTH = 255;

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'description' && value.length > MAX_REASON_LENGTH) {
      return; // không cập nhật nếu quá 255 ký tự
    }

    if (name === "eventType") {
      const eventTypeValue = Number(value);
      const {
        missingCheckInUserIds = [],
        missingCheckOutUserIds = [],
      } = preselectedUserIds || {};

      const sourceIds =
        eventTypeValue === 1
          ? missingCheckInUserIds
          : missingCheckOutUserIds;

      setFormData((prev) => ({
        ...prev,
        eventType: eventTypeValue,
        users: [...new Set((sourceIds || []).filter(Boolean))],
      }));

      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  useEffect(() => {
    const valid =
      !!formData.eventType &&
      Array.isArray(formData.users) &&
      formData.users.length > 0 &&
      !!genaralSettingTimeZone &&
      !!formData.shift &&
      !!formData.selectDate &&
      !!formData.selectTime;
    setIsFormValid(valid);
  }, [formData]);

  const handleSubmitAddEvents = async () => {
    const userIds = Array.isArray(formData.users)
      ? formData.users.filter(Boolean)
      : [];

    if (userIds.length === 0) {
      toast.error(t("common.no-employee"));
      return;
    }

    // Chuẩn bị chung cho từng item
    const attendanceDateTime = buildAttendanceDateTimeUTC(
      formData.selectDate,
      formData.selectTime,
    );

    if (!attendanceDateTime) {
      toast.error("Invalid attendance time");
      return;
    }

    const common = {
      eventType: Number(formData.eventType),
      attendanceDateTime,            // ✅ NEW
      shift: formData.shift,
      useDifferentShiftDate: false,
      description: formData.description?.trim() || "",
      langId: localStorage.getItem("i18nextLngId"),
      latitude: "",
      longitude: ""
    };

    // ✅ Build 1 payload dạng danh sách (array các item)
    const dataList = userIds.map((userId) => ({
      ...common,
      userId,
    }));

    if (TEST_MODE) {
      toast.info(`Test mode: built ${dataList.length} item(s).`);
      handleCloseDialog();
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await addEventLogBulk(dataList);

      if (res?.data?.responseCode === 0) {
        toast.success(t("common.add-event-log-successfully"));
        onRefresh?.();
        handleCloseDialog();
      } else {
        toast.error(res?.data?.message || "Error");
      }
    } catch (error) {
      let msg = error?.response?.data?.message || error?.message || "Error";
      const match = String(msg).match(/{.*}/);
      if (match) {
        try { msg = JSON.parse(match[0])?.message || msg; } catch { }
      }
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedShifts = useMemo(() => {
    if (!Array.isArray(shiftsData)) return [];

    // shift mới tạo → shiftId lớn hơn → cho lên trước
    return [...shiftsData].sort((a, b) => b.shiftId - a.shiftId);
  }, [shiftsData]);

  useEffect(() => {
    if (!sortedShifts.length) return;

    // 1️⃣ Có ca truyền từ ngoài vào → ưu tiên
    if (preselectedShiftIds) {
      // nếu là mảng (multiple)
      if (Array.isArray(preselectedShiftIds) && preselectedShiftIds.length > 0) {
        setFormData((prev) => ({
          ...prev,
          shift: preselectedShiftIds[0], // dialog chỉ chọn 1 ca
        }));
        return;
      }

      // nếu là 1 shiftId
      setFormData((prev) => ({
        ...prev,
        shift: preselectedShiftIds,
      }));
      return;
    }

    // 2️⃣ Không có → fallback chọn ca mới nhất
    setFormData((prev) => ({
      ...prev,
      shift: sortedShifts[0].shiftId,
    }));
  }, [sortedShifts, preselectedShiftIds]);


  useEffect(() => {
    if (!preselectedUserIds) return;

    const {
      missingCheckInUserIds = [],
      missingCheckOutUserIds = [],
    } = preselectedUserIds;

    setFormData((prev) => {
      const sourceIds =
        Number(prev.eventType) === 1
          ? missingCheckInUserIds
          : missingCheckOutUserIds;

      return {
        ...prev,
        users: [...new Set((sourceIds || []).filter(Boolean))],
      };
    });
  }, [preselectedUserIds]);


  return (
    <CustomDialog
      open
      transitionDuration={300}
      maxWidth={"xl"}
      loading={isSubmitting}
      onClose={handleCloseDialog}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("button.add-missing-checkout")}</p>
          </div>
          <GrayIconButton onClick={handleCloseDialog} disabled={isSubmitting}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        {/* Event type */}
        <div className="pt-3">
          <p>
            {t("common.event-type")}
            <span className="text-[#EF4444] ml-1">*</span>
          </p>
          <FormControl
            component="fieldset"
            sx={{ ".MuiFormGroup-root.MuiFormGroup-row": { gap: "20px" } }}
          >
            <RadioGroup
              row
              aria-label="Event type"
              name="eventType"
              value={formData.eventType}
              onChange={handleInputChange}
            >
              <FormControlLabel
                value={1}
                control={<Radio size="small" />}
                label={t("attendance.CHECK_IN")}
              />
              <FormControlLabel
                value={2}
                control={<Radio size="small" />}
                label={t("attendance.CHECK_OUT")}
              />
            </RadioGroup>
          </FormControl>
        </div>

        <div className="grid grid-cols-12 md:gap-x-10 gap-y-4">
          {/* ✅ Multi user bằng CustomSelectRosterSelectUser */}
          <div className="col-span-12 md:col-span-12 mt-6 space-y-[8px]">
            <label className="mb-2.5">
              {t("common.find-user")}
              <span className="text-[#EF4444] ml-1">*</span>
              {Array.isArray(formData.users) && formData.users.length > 0 && (
                <span className="ml-1 text-blue-600">
                  ({formData.users.length})
                </span>
              )}
            </label>
            <CustomSelectRosterSelectUser
              className="!w-full"
              name="users"
              id="users"
              multiple={true}
              dataType="user"
              options={filteredMissingUsers}
              value={Array.isArray(formData.users) ? formData.users : []} // mảng userId
              onChange={handleInputChange} // nhận { target: { name, value } }
              position="bottom"
            // onRemoveItem={(removedId) => { /* nếu muốn bắt sự kiện remove chip */ }}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <label>{t("common.date-time")}</label>
            <div className="mt-2">
              <CustomDateTimePicker
                mode="singleTime"
                value={
                  formData.selectDate && formData.selectTime
                    ? dayjs(
                      `${dayjs(formData.selectDate).format("YYYY-MM-DD")} ${normalizeTime(formData.selectTime) || formData.selectTime}`
                    ).toDate()
                    : formData.selectDate || null
                }
                dateTimeFormat={dateTimeFormat}
                onChange={(newDateTime) => {
                  const d = dayjs(newDateTime);
                  setFormData((prev) => ({
                    ...prev,
                    selectDate: d.toDate(),
                    selectTime: d.format("HH:mm:ss"),
                  }));
                }}
              />
            </div>
          </div>

          <div className="col-span-12 md:col-span-6">
            <label>
              {t("common.shift")}
              <span className="text-[#EF4444] ml-1">*</span>
            </label>
            <CustomSelect
              name="shift"
              value={formData.shift}
              onChange={handleInputChange}
              options={sortedShifts}
              className="mt-2"
              dataType="shift"
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <label>{t("common.description")}</label>
            <CustomInput
              name="description"
              inputType="text"
              value={formData.description}
              handleInputChange={(n, v) => handleDataChange("description", v)}
              labelEnable={false}
              className="mt-2"
              placeholder={t("common.description")}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isSubmitting}>
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSubmitAddEvents}
            disabled={isSubmitting || !isFormValid}
          >
            <p>{t("button.add-event")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default AddMultiEventDialog;
